const API_BASE = '/api'

async function authFetch(path: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...options })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  return res
}

async function failFriendly(res: Response, fallback: string): Promise<never> {
  let msg = fallback
  try {
    const body = await res.json()
    if (body?.error && typeof body.error === 'string') msg = body.error
  } catch {
    void 0
  }
  throw new Error(msg)
}

export type LedgerEventType = 'probe' | 'decision' | 'promotion' | 'task_closed'

export interface LedgerEvent {
  id: string
  org_id: string
  user_id?: string
  can_edit?: boolean
  event_type: LedgerEventType
  idea_id?: string
  parent_idea_id?: string
  thesis_id?: string
  family_id?: string
  relation_type?: string
  related_thesis_id?: string
  task_id?: string
  run_id?: string
  summary: string
  payload: Record<string, unknown>
  tags: string[]
  created_at: string
}

export interface LedgerEventWrite {
  event_type: LedgerEventType
  idea_id?: string | null
  thesis_id?: string | null
  family_id?: string | null
  task_id?: string | null
  summary: string
  payload?: Record<string, unknown>
  tags?: string[]
}

export async function fetchLedger(params?: { event_type?: string; limit?: number }): Promise<LedgerEvent[]> {
  const qs = new URLSearchParams()
  if (params?.event_type) qs.set('event_type', params.event_type)
  if (params?.limit) qs.set('limit', String(params.limit))
  const suffix = qs.size > 0 ? `?${qs}` : ''
  const res = await authFetch(`/research/ledger${suffix}`)
  if (!res.ok) await failFriendly(res, "Couldn't load the ledger.")
  const body = await res.json()
  return body.ledger ?? []
}

export async function createLedgerEvent(body: LedgerEventWrite): Promise<LedgerEvent> {
  const res = await authFetch('/research/ledger', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await failFriendly(res, "Couldn't create the ledger event.")
  return res.json()
}

export async function updateLedgerEvent(id: string, body: LedgerEventWrite): Promise<LedgerEvent> {
  const res = await authFetch(`/research/ledger/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await failFriendly(res, "Couldn't update the ledger event.")
  return res.json()
}

export async function deleteLedgerEvent(id: string): Promise<void> {
  const res = await authFetch(`/research/ledger/${id}`, { method: 'DELETE' })
  if (!res.ok) await failFriendly(res, "Couldn't delete the ledger event.")
}
